<ng-template #errorTooltip>
  @if (hasError && showErrorTooltip) {
    <app-custom-error-tooltip
      [tooltipWidth]="tooltipWidth"
      [tooltipText]="getErrorMessage()"
      (tooltipCloseClicked)="closeErrorTooltip()">
    </app-custom-error-tooltip>
  }
</ng-template>

@if (config.editTemplate) {
  <ng-container
    [ngTemplateOutlet]="config.editTemplate"
    [ngTemplateOutletContext]="{
      $implicit: control || null,
      row: row,
      rowIndex: rowIndex,
      formGroup: formGroup
    }">
  </ng-container>
}

@if (!config.editTemplate && config.type === 'text') {
  <span
    class="ibc-body-text-medium"
    [class.text-hyperlink]="shouldShowHyperlink"
    [class.cursor]="shouldShowHyperlink"
    [attr.aria-hidden]="shouldShowHyperlink ? 'true' : null"
    tabindex="-1"
    (click)="shouldShowHyperlink ? onLinkClick() : null"
    (keydown)="$event.stopPropagation()">
    {{ displayValue }}
  </span>
}

@if (!config.editTemplate && control && config.type !== 'text') {
  @switch (config.type) {
    @case ('input') {
      <mat-form-field appearance="outline" class="mat-sm {{ config.cssClass }}">
        @switch (inputSubType) {
          @case ('basic') {
            @if (shouldTrim) {
              <input
                matInput
                appTrimInput
                [formControl]="control"
                [placeholder]="config.placeholder ?? ''"
                (input)="onInputChange()"
                (blur)="onInputBlur()" />
            } @else {
              <input
                matInput
                [formControl]="control"
                [placeholder]="config.placeholder ?? ''"
                (input)="onInputChange()"
                (blur)="onInputBlur()" />
            }
          }

          @case ('alphanumeric') {
            @if (shouldUppercase) {
              <input
                matInput
                [formControl]="control"
                [placeholder]="config.placeholder ?? ''"
                [appAlphabetsWithNumbers]="maxLength"
                appUppercase
                (input)="onInputChange()"
                (blur)="onInputBlur()" />
            } @else {
              <input
                matInput
                [formControl]="control"
                [placeholder]="config.placeholder ?? ''"
                [appAlphabetsWithNumbers]="maxLength"
                (input)="onInputChange()"
                (blur)="onInputBlur()" />
            }
          }

          @case ('alphanumericHyphen') {
            <input
              matInput
              [formControl]="control"
              [placeholder]="config.placeholder ?? ''"
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }

          @case ('decimal') {
            <input
              matInput
              [formControl]="control"
              [placeholder]="config.placeholder ?? ''"
              [appDigitWithTwoDecimalNumber]="maxDigits"
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }

          @case ('number') {
            <input
              matInput
              [formControl]="control"
              [placeholder]="config.placeholder ?? ''"
              type="number"
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }

          @case ('postalCode') {
            <input
              matInput
              [formControl]="control"
              [placeholder]="config.placeholder ?? ''"
              appPostalCode
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }

          @case ('numbersWithHyphen') {
            <input
              matInput
              [formControl]="control"
              [placeholder]="config.placeholder ?? ''"
              appNumbersWithHyphen
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }

          @case ('timeHhMm') {
            <input
              matInput
              appTimeHhMmFormat
              [formControl]="control"
              [placeholder]="config.placeholder ?? 'HH:MM'"
              (input)="onInputChange()"
              (blur)="onInputBlur()" />
          }
        }

        @if (showInfoIcon) {
          <app-svg-icon
            matSuffix
            [iconName]="'infoCircle'"
            [isStaticSvg]="true"
            [isFrom]="'Page'"
            class="info-icon hawb-info-block cursor"
            [matTooltipPosition]="infoTooltipPosition"
            [matTooltip]="infoTooltipText">
          </app-svg-icon>
        }

        @if (hasError) {
          <app-svg-icon
            matSuffix
            [iconName]="'infoCircle'"
            [isStaticSvg]="true"
            [isFrom]="'Page'"
            class="cursor info-icon-error"
            (click)="onErrorIconClick($event)"
            (keydown)="$event.stopPropagation()">
          </app-svg-icon>
        }
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }

    @case ('select') {
      <mat-form-field
        appearance="outline"
        [class.mat-form-field-invalid]="hasError"
        class="mat-sm mat-select-variants {{ config.cssClass }}">
        <mat-icon matPrefix class="dropdown-icon">
          {{ actionIcons.keyboardDownArrow }}
        </mat-icon>
        <mat-select
          [formControl]="control"
          [multiple]="config.multiple"
          (selectionChange)="onSelectChange($event)">
          @for (option of resolvedOptions; track option) {
            <mat-option [value]="option[optionValue] ?? option">
              {{ option[optionDisplay] ?? option }}
            </mat-option>
          }
        </mat-select>

        <div matSuffix class="field-suffix-icons">
          <app-field-clear-suffix
            [large]="false"
            [control]="control"
            [multiple]="config.multiple ?? false"
            [autocomplete]="false"
            (cleared)="onFieldCleared()" />

          @if (hasError) {
            <app-svg-icon
              [iconName]="'infoCircle'"
              [isStaticSvg]="true"
              [isFrom]="'Page'"
              class="cursor info-icon-error"
              (click)="onErrorIconClick($event)"
              (keydown)="$event.stopPropagation()">
            </app-svg-icon>
          }
        </div>
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }

    @case ('autocomplete') {
      <mat-form-field
        appearance="outline"
        [class.mat-form-field-invalid]="hasError"
        class="mat-sm mat-form-field-disabled {{ config.cssClass }}">
        <mat-icon matPrefix class="dropdown-icon">
          {{ actionIcons.keyboardDownArrow }}
        </mat-icon>
        <input
          matInput
          #autocompleteTrigger="matAutocompleteTrigger"
          [formControl]="control"
          [placeholder]="config.placeholder ?? ''"
          [matAutocomplete]="auto"
          (input)="onAutocompleteInput($event)" />
        <mat-autocomplete
          #auto="matAutocomplete"
          (keydown)="$event.stopPropagation()"
          (optionSelected)="onAutocompleteSelected($event)">
          @for (option of autocompleteOptions$ | async; track option) {
            <mat-option [value]="option[optionDisplay]">
              {{ option[optionDisplay] }}
            </mat-option>
          }
        </mat-autocomplete>

        <div matSuffix class="field-suffix-icons">
          <app-field-clear-suffix
            [large]="false"
            [control]="control"
            [autocomplete]="true"
            (cleared)="onFieldCleared()" />

          @if (hasError) {
            <app-svg-icon
              [iconName]="'infoCircle'"
              [isStaticSvg]="true"
              [isFrom]="'Page'"
              class="cursor info-icon-error"
              (click)="onErrorIconClick($event)"
              (keydown)="$event.stopPropagation()">
            </app-svg-icon>
          }
        </div>
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }

    @case ('time') {
      <mat-form-field appearance="outline" class="mat-sm {{ config.cssClass }}">
        <input
          matInput
          type="time"
          [formControl]="control"
          [placeholder]="config.placeholder ?? 'HH:MM'"
          (input)="onInputChange()"
          (blur)="onInputBlur()" />

        @if (hasError) {
          <app-svg-icon
            matSuffix
            [iconName]="'infoCircle'"
            [isStaticSvg]="true"
            [isFrom]="'Page'"
            class="cursor info-icon-error"
            (click)="onErrorIconClick($event)"
            (keydown)="$event.stopPropagation()">
          </app-svg-icon>
        }
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }

    @case ('number') {
      <mat-form-field appearance="outline" class="mat-sm {{ config.cssClass }}">
        <input
          matInput
          type="number"
          [formControl]="control"
          [placeholder]="config.placeholder ?? ''"
          (input)="onInputChange()"
          (blur)="onInputBlur()" />

        @if (hasError) {
          <app-svg-icon
            matSuffix
            [iconName]="'infoCircle'"
            [isStaticSvg]="true"
            [isFrom]="'Page'"
            class="cursor info-icon-error"
            (click)="onErrorIconClick($event)"
            (keydown)="$event.stopPropagation()">
          </app-svg-icon>
        }
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }

    @case ('multiselect') {
      <mat-form-field
        appearance="outline"
        [class.mat-form-field-invalid]="hasError"
        class="mat-sm mat-select-variants {{ config.cssClass }}">
        <mat-icon matPrefix class="dropdown-icon">
          {{ actionIcons.keyboardDownArrow }}
        </mat-icon>
        <mat-select
          [formControl]="control"
          [multiple]="true"
          (selectionChange)="onSelectChange($event)">
          @for (option of config.options; track option) {
            <mat-option [value]="option[optionValue] ?? option">
              {{ option[optionDisplay] ?? option }}
            </mat-option>
          }
        </mat-select>

        <div matSuffix class="field-suffix-icons">
          <app-field-clear-suffix
            [large]="false"
            [control]="control"
            [multiple]="true"
            (cleared)="onFieldCleared()" />

          @if (hasError) {
            <app-svg-icon
              [iconName]="'infoCircle'"
              [isStaticSvg]="true"
              [isFrom]="'Page'"
              class="cursor info-icon-error"
              (click)="onErrorIconClick($event)"
              (keydown)="$event.stopPropagation()">
            </app-svg-icon>
          }
        </div>
        <ng-container *ngTemplateOutlet="errorTooltip"></ng-container>
      </mat-form-field>
    }
  }
}
